import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { PackingPlanningService } from 'src/app/services/packing-planning/packing-planning.service';
import { PackingPlanningModel } from 'src/app/models/PackingPlanningModel';

@Component({
  selector: 'app-planning-table-form',
  templateUrl: './planning-table-form.component.html',
  styleUrl: './planning-table-form.component.css'
})
export class PlanningTableFormComponent {
  form: FormGroup;
  companies: string[] = ['SESE', 'LOTS']
  tripTypes: string[] = ['RECEBIMENTO', 'EXPEDIÇÃO', 'REUSE', 'VAZIO']

  @Input() receivedTrip!: PackingPlanningModel | null;
  @Output() closeForm = new EventEmitter<void>();
  @Output() dataUpdated = new EventEmitter<void>();

  apiUrl: string = environment.apiLink;

  constructor(
    private formBuilder: FormBuilder,
    private packingService: PackingPlanningService,
  ) {
    this.form = this.formBuilder.group({
      tripName: [null, [Validators.required]],
      classification: ["time-table"],
      arrivalLCB: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      departureLCB: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      tripType: [null],
      company: [null],
    })
  }

  ngOnInit() {
    this.tripTypeCheck()
    this.handleTripTypeChange()
  }

  ngOnChanges() {
    if (this.receivedTrip) {
      this.updateFormValues(this.receivedTrip)
      this.checkAndUpdateTripType(this.receivedTrip)
      this.handleTripTypeChange()
    } else this.resetForm()
  }

  private updateFormValues(trip: PackingPlanningModel) {
    this.form.patchValue({
      tripName: trip.trip,
      classification: trip.classification,
      company: trip.company,
      tripType: trip.category,
      arrivalLCB: trip.arrivalTime,
      departureLCB: trip.departureTime
    });
    this.form.controls['tripName'].disable()
  }

  private checkAndUpdateTripType(trip: PackingPlanningModel) {
    if (this.isMG(trip)) {
      this.form.get('classification')?.setValue('mg')
    } else if (this.isContainer(trip)) {
      this.form.get('classification')?.setValue('container')
    }
  }

  private handleTripTypeChange() {
    const selectedValue = this.form.get('classification')?.value;

    if (selectedValue === 'time-table') {
      this.toggleControls(['tripType'], false)
      this.form.controls['tripType'].disable()
    } else if (selectedValue === 'mg' || selectedValue === 'container') {
      this.toggleControls(['company'], false)
    }
  }

  private tripTypeCheck() {
    this.form.get('classification')?.valueChanges.subscribe(selectedValue => {
      this.updateValidatorsBasedOnTripType(selectedValue);
    });
  }

  private isMG(trip: PackingPlanningModel): boolean {
    return trip.category == "mg"
  }

  private isContainer(trip: PackingPlanningModel): boolean {
    return trip.category == "container"
  }

  private updateValidatorsBasedOnTripType(tripType: string) {
    if (tripType === 'time-table') {
      this.disableAndClearValidators(['tripType'])
      this.enableAndSetValidators('company', [Validators.required])
    } else {
      this.disableAndClearValidators(['company'])
      this.enableAndSetValidators('tripType', [Validators.required])
    }

    this.form.updateValueAndValidity()
  }

  private toggleControls(controlNames: string[], enable: boolean, validators: any[] = []) {
    controlNames.forEach(controlName => {
      const control = this.form.get(controlName)
      if (enable) {
        control?.enable()
        control?.setValidators(validators)
      } else {
        control?.disable()
        control?.clearValidators()
        control?.reset()
      }
      control?.updateValueAndValidity()
    })
  }

  disableAndClearValidators(controlNames: string[]) {
    this.toggleControls(controlNames, false)
  }

  enableAndSetValidators(controlName: string, validators: any[]) {
    this.toggleControls([controlName], true, validators)
  }

  submitForm() {
    const rawValue = this.form.getRawValue();
    const trimmedTripName = rawValue.tripName?.trim();
    const classification = rawValue.classification;

    this.form.get('tripName')?.setValue(trimmedTripName)

    if (!trimmedTripName) {
      alert("Nome da viagem é obrigatório!")
      return
    }

    if (classification === "time-table" && !rawValue.company) {
      alert("Campo 'Empresa' é obrigatório para time-table!")
      return
    }

    if ((classification === "mg" || classification === "container") && !rawValue.tripType) {
      alert("Campo 'Tipo' é obrigatório para MG ou Container!")
      return
    }

    if (!rawValue.arrivalLCB || rawValue.arrivalLCB.length !== 5 ||
      !rawValue.departureLCB || rawValue.departureLCB.length !== 5
    ) {
      alert("Horários de chegada e saída devem estar no formato HH:mm (5 caracteres)");
      return;
    }

    if (this.form.invalid) {
      alert("Preencha os campos obrigatórios!")
      return
    }

    const newTrip = {
      trip: trimmedTripName,
      classification: classification,
      company: rawValue.company,
      category: rawValue.tripType,
      arrivalTime: rawValue.arrivalLCB,
      departureTime: rawValue.departureLCB,
    };

    this.packingService.createPlanningTrip(newTrip).subscribe({
      next: () => {
        alert("Dados inseridos com sucesso");
        this.resetForm();
        this.dataUpdated.emit()
      },
      error: (error) => {
        console.error("Erro ao enviar os dados: ", error);
        alert("Erro ao inserir dados");
      },
    });
  }

  updateTrip() {
    if (this.form.valid) {

      const rawData = this.form.getRawValue();

      const updatedTrip = {
        trip: rawData.tripName,
        classification: rawData.classification,
        company: rawData.company,
        category: rawData.tripType,
        arrivalTime: rawData.arrivalLCB,
        departureTime: rawData.departureLCB,
      };

      this.packingService.updatePlanningTrip(updatedTrip, this.receivedTrip!.id).subscribe({
        next: () => {
          alert("Viagem atualizada");
          this.resetForm();
          this.dataUpdated.emit()
        },
        error: (error) => {
          console.error("Erro ao atualizar dados: ", error);
          alert("Erro ao atualizar viagem");
        },
      });
    } else {
      alert("Preencha os campos corretamente");
    }
  }

  resetForm() {
    this.form.enable()
    this.form.reset({
      classification: "time-table",
      tripName: null,
      arrivalTime: null,
      departureTime: null,
      tripType: null,
      company: null,
    });
    this.receivedTrip = null
  }

  onClose() {
    this.resetForm()
    this.closeForm.emit()
  }

  isNewTrip(): boolean {
    return this.receivedTrip == null;
  }

  formatTime(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, ''); // Remove caracteres não numéricos

    if (value.length >= 3) {
      value = value.substring(0, 2) + ':' + value.substring(2, 4); // Adiciona ":"
    }

    input.value = value.substring(0, 5); // Limita ao formato HH:mm
    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      this.form.controls[controlName].setValue(value);
    }
  }

  stopPropagation(event: Event): void {
    event.stopPropagation()
  }
}
